/**
 * @file legal-targets.ts
 * @description Список «Документ или группа документов» для выбора цели
 * ссылки (phase19.16, партия «Юридические документы»; uiux-legal-steps-b-c
 * §8.1, §8.2; SB139, SB141, SB142): группы сверху, документы ниже, у каждой
 * строки вид и состояние словами; строка «Создать» автоматической группы
 * сертификатов, если её ещё нет.
 *
 * Чистые функции — один список для пункта меню, кнопки секции и окна
 * «+ Добавить ссылку на документ» подвала; проверяются тестом.
 */

import {
  LEGAL_CERTIFICATES_GROUP_DEFAULTS,
  LEGAL_FOOTER_PLATFORM_SLUGS,
  LEGAL_INDEX_LABEL,
  legalDocumentStatusLabel,
} from "@chaiberi/shared";

import type { ComboboxOption } from "../../components/ui/combobox";
import type { LegalTarget } from "../api-legal-targets";

/** Значение строки «Создать автоматическую группу сертификатов» (не id документа). */
export const CREATE_CERTIFICATES_VALUE = "create:certificates";

/** Значение строки «Все документы» в окне подвала (своя строка `allDocuments`). */
export const ALL_DOCUMENTS_VALUE = "all-documents";

/** Заголовки групп списка (без ручного капса — правило контракта UI). */
export const LEGAL_TARGET_GROUPS = { groups: "Группы", documents: "Документы", pages: "Страница" } as const;

/** Подпись вида в строке цели. */
const KIND_SHORT: Readonly<Record<LegalTarget["kind"], string>> = { TEXT: "Текст", PDF: "PDF", GROUP: "Группа" };

/** Автоматическая группа сертификатов (одна на магазин). */
export const isCertificatesGroup = (t: LegalTarget): boolean => t.kind === "GROUP" && t.groupSource === "PRODUCER_CERTIFICATES";

/** Группа опубликована, но на сайте не видна: в ней нет видимых документов (SB16). */
export const isEmptyLegalGroup = (t: LegalTarget): boolean => t.kind === "GROUP" && !t.visibleOnSite;

/**
 * Пояснение под названием: «Текст · опубликован», «PDF · черновик — на
 * сайте не виден», «3 документа · пуста — на сайте не видна»,
 * «автоматически · 5 на сайте».
 */
export function legalTargetDescription(t: LegalTarget): string {
  if (t.kind === "GROUP") {
    const visible = t.members?.visible ?? 0;
    const head = isCertificatesGroup(t) ? "автоматически" : `${t.members?.total ?? 0} док.`;
    if (t.status === "draft" || t.status === "unpublished") return `${head} · не опубликована — на сайте не видна`;
    return t.visibleOnSite ? `${head} · ${visible} на сайте` : `${head} · пуста — на сайте не видна`;
  }
  const state = legalDocumentStatusLabel(t.status, { kind: t.kind }).toLowerCase();
  return `${KIND_SHORT[t.kind]} · ${state}${t.visibleOnSite ? "" : " — на сайте не виден"}`;
}

export interface LegalTargetOptionsInput {
  targets: readonly LegalTarget[];
  /** Можно ли создать автоматическую группу (право `SHOP_MANAGE_SETTINGS`). */
  canCreateCertificates: boolean;
  /** Подвал: без документов ссылок платформы (оферта, политика, возврат — §8.2). */
  footer?: boolean;
  /** Подвал: уже стоящие в «Ссылках» (id и `ALL_DOCUMENTS_VALUE`) — неактивны. */
  taken?: ReadonlySet<string>;
}

/** Пометка уже добавленной строки (§8.2). */
const TAKEN_NOTE = "уже в «Ссылках»";

/** Строки списка: группы (и «Создать» автоматической), затем документы; в подвале — ещё «Все документы». */
export function legalTargetOptions({ targets, canCreateCertificates, footer = false, taken }: LegalTargetOptionsInput): ComboboxOption[] {
  const byTitle = (a: LegalTarget, b: LegalTarget) => a.title.localeCompare(b.title, "ru");
  const toOption = (t: LegalTarget, group: string): ComboboxOption => {
    const isTaken = taken?.has(t.id) ?? false;
    return { value: t.id, label: t.title, group, disabled: isTaken, description: isTaken ? TAKEN_NOTE : legalTargetDescription(t) };
  };
  const hidden: ReadonlySet<string> = new Set(footer ? LEGAL_FOOTER_PLATFORM_SLUGS : []);
  const groups = targets.filter((t) => t.kind === "GROUP").sort(byTitle);
  const docs = targets.filter((t) => t.kind !== "GROUP" && !hidden.has(t.slug)).sort(byTitle);
  const out: ComboboxOption[] = [];
  if (!groups.some(isCertificatesGroup) && canCreateCertificates) {
    out.push({
      value: CREATE_CERTIFICATES_VALUE,
      label: `+ Создать «${LEGAL_CERTIFICATES_GROUP_DEFAULTS.title}»`,
      description: "соберётся из карточек производителей",
      group: LEGAL_TARGET_GROUPS.groups,
    });
  }
  out.push(...groups.map((t) => toOption(t, LEGAL_TARGET_GROUPS.groups)));
  out.push(...docs.map((t) => toOption(t, LEGAL_TARGET_GROUPS.documents)));
  if (footer) {
    const isTaken = taken?.has(ALL_DOCUMENTS_VALUE) ?? false;
    out.push({
      value: ALL_DOCUMENTS_VALUE,
      label: LEGAL_INDEX_LABEL,
      description: isTaken ? TAKEN_NOTE : "страница со всеми опубликованными документами",
      group: LEGAL_TARGET_GROUPS.pages,
      disabled: isTaken,
    });
  }
  return out;
}

/** Подсказка без права создавать автоматическую группу (§8.1). */
export const CERTIFICATES_NO_RIGHT_HINT = "Страницу сертификатов создаёт сотрудник с правом настроек магазина.";

/** Подсказка под полем, если выбрана пустая группа (§8.1). */
export const EMPTY_GROUP_HINT = "Сейчас группа пуста — ссылка на сайте не появится, пока в ней не будет опубликованного документа.";
